import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { BehaviorSubject, Observable } from 'rxjs';

import { EndpointParams } from '@models/index';
import { HttpService } from '@services/http.service';
import { FeeShopItem } from '@models/fee/shop/fee-shop-item';
import { buildFeeShopItem } from '@models/fee/shop/fee-shop-item.factory';
import { Exceptions } from '@enumerates/exceptions';

@Injectable()
export class ShopService extends HttpService {
    private readonly defaultEndpointParams: EndpointParams = { app: 'payments', version: 1 };

    private readonly itemsSubject = new BehaviorSubject<FeeShopItem[]>([]);
    private readonly isLoadingDataSubject = new BehaviorSubject<boolean>(true);
    private readonly dataStore: { items: FeeShopItem[] } = { items: [] };

    readonly items = this.itemsSubject.asObservable();
    readonly isLoading = this.isLoadingDataSubject.asObservable();

    list(): void {
        this.loading(true);
        this.listShop().subscribe(
            () => {
                this.next();
                this.loading(false);
            }
        );
    }

    private listShop(): Observable<boolean> {
        return new Observable<boolean>(
            observable => {
                this.get('fee/member/shop', this.defaultEndpointParams).subscribe(
                    data => {
                        this.dataStore.items = (data ?? []).map((d: any) => buildFeeShopItem(d));
                        observable.next(true);
                    }, () => observable.next(false)
                );
            }
        );
    }

    private next(): void {
        this.itemsSubject.next([ ...this.dataStore.items ]);
    }

    private loading(value: boolean): void {
        this.isLoadingDataSubject.next(value);
    }

    getById(id: string, fromContractingLink = false): Observable<FeeShopItem> {
        const params = fromContractingLink ? new HttpParams({ fromString: 'fromContractingLink=true' }) : undefined;

        return new Observable<FeeShopItem>(observer => {
            this.get(`fee/member/shop/${id}`, this.defaultEndpointParams, params).subscribe({
                next: (data: any) => {
                    observer.next(buildFeeShopItem(data));
                },
                error: (error) => observer.error(this.mapItemError(error)),
                complete: () => observer.complete()
            });
        });
    }

    getPublicById(id: string, publicToken: string, fromContractingLink = false): Observable<FeeShopItem> {
        let params = new HttpParams({ fromString: `centerToken=${publicToken}` });
        if (fromContractingLink) {
            params = params.set('fromContractingLink', 'true');
        }

        return new Observable<FeeShopItem>(observer => {
            this.get(`public/fee/shop/${id}`, this.defaultEndpointParams, params).subscribe({
                next: (data: any) => {
                    observer.next(buildFeeShopItem(data));
                },
                error: (error) => observer.error(this.mapItemError(error)),
                complete: () => observer.complete()
            });
        });
    }

    private mapItemError(error: any): string {
        return error?.error?.errors?.[0]?.code === Exceptions.contractingLinkDisabled ? 'product_not_available' : 'error_loading_item';
    }
}
